import React, { useEffect, useState, useCallback } from 'react'
import {
    Grid, Box, Paper, Chip, Typography, Divider, Tooltip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Button, Checkbox, FormControlLabel, List, ListItem, ListItemText, CircularProgress
} from "@mui/material"
import Pagination from '@mui/material/Pagination'
import * as actions from "./actions"
import moment from "moment"
import { Tag, User, Mail, Phone, Calendar, Download, Trash2, Copy, ClipboardCheck } from 'lucide-react'
import _ from "lodash"
import { toastError, toastSuccess, copyToClipboard } from '../../utils'
import CustomShowModal from '../Common/customShowModal'
import SearchInput from '../../components/SearchInput'
import EmptyState from '../../components/EmptyState'
import StatusBadge from '../../components/StatusBadge'
import ActionButton from '../../components/ActionButton'

function CopyMeta({ icon, label, value }) {
    const [copied, setCopied] = useState(false);
    if (!value || value === '-') {
        return (
            <Tooltip title={label} arrow placement="top">
                <span className="approved-release-card__meta-item">
                    {icon} -
                </span>
            </Tooltip>
        );
    }
    return (
        <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
            <span
                className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
                onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            >
                {icon} {value}
                {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
            </span>
        </Tooltip>
    );
}

export default function LabelRequests(props) {

    const [requestsNew, setRequestsNew] = useState([])
    const [requestsOld, setRequestsOld] = useState([])
    const [countOld, setCountOld] = useState(0)

    const [filters, setFilters] = useState({
        limit: 20,
        offset: 0,
        page: 1,
        search: '',
    })

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        action: null,
        reviewId: null
    })
    const [assignModal, setAssignModal] = useState({
        open: false,
        userId: null
    })
    const [subAdmins, setSubAdmins] = useState([])
    const [selectedSubAdmins, setSelectedSubAdmins] = useState([])
    const [assignLoading, setAssignLoading] = useState(false)
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        reviewId: null
    })

    const delaySearch = useCallback(
        _.debounce((filters) => onGetHistory(filters), 500),
        []
    )

    useEffect(() => {
        document.title = 'LR | Label Requests';
        return () => { document.title = 'Fronic Media Admin'; };
    }, []);

    useEffect(() => {
        onGetNewRequests()
        onGetHistory(filters)
    }, [])

    const onGetNewRequests = () => {
        props.setLoader(true)
        actions.showLabelRequestsInReview({ status: 'in_review', limit: 200, offset: 0 }).then(res => {
            props.setLoader(false)
            setRequestsNew(res.body || [])
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to load label requests')
        })
    }

    const onGetHistory = (f) => {
        props.setLoader(true)
        actions.showLabelRequestsInReview({ status: 'approved,rejected', limit: f.limit, offset: f.offset, search: f.search }).then(res => {
            props.setLoader(false)
            setRequestsOld(res.body || [])
            setCountOld(res.total || 0)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to load label request history')
        })
    }

    const onPageChange = (event, page) => {
        const offset = (page - 1) * filters.limit
        const newFilters = { ...filters, page, offset }
        setFilters(newFilters)
        onGetHistory(newFilters)
    }

    const onChangeSearch = (e) => {
        const newFilters = { ...filters, search: e.target.value, page: 1, offset: 0 }
        setFilters(newFilters)
        delaySearch(newFilters)
    }

    const openConfirmModal = (reviewId, action) => {
        setConfirmModal({ open: true, action, reviewId })
    }

    const closeConfirmModal = () => {
        setConfirmModal({ open: false, action: null, reviewId: null })
    }

    const onActionRequest = () => {
        const { reviewId, action } = confirmModal
        closeConfirmModal()
        props.setLoader(true)
        actions.actionLabelRequestAdmin({ review_id: reviewId, action }).then(res => {
            props.setLoader(false)
            toastSuccess(res.message || `Request ${action} successfully`)
            if (action === 'approved' && res.body?.user_id) {
                fetchSubAdminsAndOpenModal(res.body.user_id)
            } else {
                onGetNewRequests()
                onGetHistory(filters)
            }
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || `Failed to ${action} request`)
        })
    }

    const fetchSubAdminsAndOpenModal = (userId) => {
        setAssignLoading(true)
        actions.getSubAdminsListAdmin().then(res => {
            setAssignLoading(false)
            setSubAdmins(res.body || [])
            setSelectedSubAdmins([])
            setAssignModal({ open: true, userId })
        }).catch(err => {
            setAssignLoading(false)
            toastError(err?.message || 'Failed to fetch sub_admins')
            onGetNewRequests()
            onGetHistory(filters)
        })
    }

    const handleSubAdminToggle = (subAdminId) => {
        setSelectedSubAdmins(prev => {
            if (prev.includes(subAdminId)) {
                return prev.filter(id => id !== subAdminId)
            } else {
                return [...prev, subAdminId]
            }
        })
    }

    const handleAssignSubmit = () => {
        if (selectedSubAdmins.length === 0) {
            toastError('Please select at least one sub_admin')
            return
        }
        props.setLoader(true)
        actions.assignUserToSubAdmins({
            user_id: assignModal.userId,
            subAdminIds: selectedSubAdmins
        }).then(res => {
            props.setLoader(false)
            toastSuccess(res.message || 'User assigned successfully')
            closeAssignModal()
            onGetNewRequests()
            onGetHistory(filters)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to assign user')
        })
    }

    const handleSkipAssign = () => {
        closeAssignModal()
        onGetNewRequests()
        onGetHistory(filters)
    }

    const closeAssignModal = () => {
        setAssignModal({ open: false, userId: null })
        setSelectedSubAdmins([])
    }

    const openDeleteModal = (reviewId) => {
        setDeleteModal({ open: true, reviewId })
    }

    const closeDeleteModal = () => {
        setDeleteModal({ open: false, reviewId: null })
    }

    const onDeleteB2BFile = () => {
        const { reviewId } = deleteModal
        closeDeleteModal()
        props.setLoader(true)
        actions.deleteB2BFileAdmin({ review_id: reviewId }).then(res => {
            props.setLoader(false)
            toastSuccess(res.message || 'B2B file deleted successfully')
            onGetHistory(filters)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to delete B2B file')
        })
    }

    let totalPages = Math.ceil(Number(countOld) / filters.limit)

    const renderRequestCard = (item, isNew) => (
        <Paper
            key={item.review_id}
            className="approved-release-card"
            style={{ cursor: 'default' }}
        >
            <div className="approved-release-card__artwork">
                <div className="approved-release-card__artwork-fallback">
                    <Tag size={20} color="#64748B" />
                    <span>Label</span>
                </div>
            </div>

            <div className="approved-release-card__body">
                <div className="approved-release-card__top">
                    <div className="approved-release-card__title-row">
                        <Typography className="approved-release-card__title" sx={{ color: '#F59E0B', fontWeight: 600 }}>
                            Requested by Label: {item.label_email || '-'}
                        </Typography>
                        {item.created_at && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#64748B', fontSize: '0.75rem', fontWeight: 400, marginLeft: 8, flexShrink: 0 }}>
                                <Calendar size={12} /> {moment(item.created_at).format('DD/MM/YYYY')}
                            </span>
                        )}
                    </div>
                    <div className="approved-release-card__status" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {isNew ? (
                            <Box display="flex" gap={0.75}>
                                <ActionButton variant="success" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => openConfirmModal(item.review_id, 'approved')}>
                                    Accept
                                </ActionButton>
                                <ActionButton variant="danger" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => openConfirmModal(item.review_id, 'rejected')}>
                                    Reject
                                </ActionButton>
                            </Box>
                        ) : (
                            <StatusBadge status={item.status} />
                        )}
                    </div>
                </div>

                <div className="approved-release-card__meta">
                    <span className="approved-release-card__id">FM-LR-{item.review_id}</span>
                    <CopyMeta icon={<User size={13} />} label="Name" value={item.name} />
                    <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
                </div>
                <div className="approved-release-card__meta" style={{ marginTop: 6 }}>
                    <CopyMeta icon={<Phone size={13} />} label="Phone" value={item.phone} />
                    {item.b2b_file && (
                        <Tooltip title="Download B2B File" arrow placement="top">
                            <IconButton
                                size="small"
                                component="a"
                                href={item.b2b_file.replace('/b2bFileUploads/', '/')}
                                target="_blank"
                                download
                                sx={{ color: '#3B82F6', '&:hover': { color: '#60A5FA' }, p: 0.5 }}
                            >
                                <Download size={14} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isNew && (
                        <Tooltip title={item.b2b_file ? "Delete B2B File" : "No B2B file"} arrow placement="top">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => openDeleteModal(item.review_id)}
                                    disabled={!item.b2b_file}
                                    sx={{
                                        color: item.b2b_file ? '#EF4444' : '#64748B',
                                        '&:hover': { color: '#F87171' },
                                        p: 0.5,
                                        '&.Mui-disabled': { color: '#3E4259' },
                                    }}
                                >
                                    <Trash2 size={14} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>
        </Paper>
    )

    return (
        <Grid container>
            {/* ========== Review Required ========== */}
            <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
                    <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
                        Review Required
                    </Typography>
                    <Chip
                        icon={<Tag size={14} />}
                        label={`${requestsNew.length} Pending`}
                        sx={{
                            backgroundColor: 'rgba(245, 158, 11, 0.12)',
                            color: '#F59E0B',
                            border: '1px solid rgba(245, 158, 11, 0.25)',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            height: 34,
                            flexShrink: 0,
                            '& .MuiChip-icon': { color: '#F59E0B' },
                        }}
                    />
                </Box>
                <Box className="approved-release-list" mb={4}>
                    {requestsNew.length ? (
                        requestsNew.map((item) => renderRequestCard(item, true))
                    ) : (
                        <Paper sx={{ p: 0 }}>
                            <table style={{ width: '100%' }}><tbody>
                                <EmptyState message="No Label Requests To Review" colSpan={1} />
                            </tbody></table>
                        </Paper>
                    )}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Divider sx={{ borderColor: 'var(--border)', mb: 3 }} />
            </Grid>

            {/* ========== Label Request History ========== */}
            <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
                    <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
                        Label Request History
                    </Typography>
                    <Chip
                        icon={<Tag size={14} />}
                        label={`${countOld} Total`}
                        sx={{
                            backgroundColor: 'rgba(108, 99, 255, 0.12)',
                            color: '#6C63FF',
                            border: '1px solid rgba(108, 99, 255, 0.25)',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            height: 34,
                            flexShrink: 0,
                            '& .MuiChip-icon': { color: '#6C63FF' },
                        }}
                    />
                </Box>
                <Box mb={3} sx={{ maxWidth: 420 }}>
                    <SearchInput
                        onChange={onChangeSearch}
                        value={filters.search}
                        placeholder="Search by name, email, or label..."
                    />
                </Box>
                <Box className="approved-release-list">
                    {requestsOld.length ? (
                        requestsOld.map((item) => renderRequestCard(item, false))
                    ) : (
                        <Paper sx={{ p: 0 }}>
                            <table style={{ width: '100%' }}><tbody>
                                <EmptyState message="No Label Request History Found" colSpan={1} />
                            </tbody></table>
                        </Paper>
                    )}
                </Box>

                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                            count={totalPages}
                            showFirstButton
                            showLastButton
                            onChange={onPageChange}
                            page={filters.page || 1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: '#94A3B8',
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(108, 99, 255, 0.2)',
                                        color: '#6C63FF',
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Grid>

            {/* ========== Confirm Action Modal ========== */}
            <CustomShowModal
                handleClose={closeConfirmModal}
                title={confirmModal.action === 'approved' ? 'Approve Label Request' : 'Reject Label Request'}
                open={confirmModal.open}
                maxWidth="sm"
            >
                <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem', mb: 2 }}>
                    {confirmModal.action === 'approved'
                        ? 'Are you sure you want to approve this label request? This will create a new user account and send login credentials to their email.'
                        : 'Are you sure you want to reject this label request? This action cannot be undone.'
                    }
                </Typography>
                <Box display="flex" justifyContent="center" gap={2} py={1}>
                    <ActionButton variant="info" onClick={closeConfirmModal}>
                        Cancel
                    </ActionButton>
                    <ActionButton
                        variant={confirmModal.action === 'approved' ? 'success' : 'danger'}
                        onClick={onActionRequest}
                    >
                        {confirmModal.action === 'approved' ? 'Approve' : 'Reject'}
                    </ActionButton>
                </Box>
            </CustomShowModal>

            {/* ========== Delete B2B File Modal ========== */}
            <CustomShowModal
                handleClose={closeDeleteModal}
                title="Delete B2B File"
                open={deleteModal.open}
                maxWidth="sm"
            >
                <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem', mb: 2 }}>
                    Are you sure you want to delete this B2B file? This action cannot be undone and the file will be permanently removed from the server.
                </Typography>
                <Box display="flex" justifyContent="center" gap={2} py={1}>
                    <ActionButton variant="info" onClick={closeDeleteModal}>
                        Cancel
                    </ActionButton>
                    <ActionButton variant="danger" onClick={onDeleteB2BFile}>
                        Delete
                    </ActionButton>
                </Box>
            </CustomShowModal>

            {/* ========== Assign Sub-Admin Modal (kept as Dialog for complex UI) ========== */}
            <Dialog open={assignModal.open} disableEscapeKeyDown maxWidth="sm" fullWidth>
                <DialogTitle>Assign User to Sub-Admins</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Select sub-admins who should have access to this newly created user:
                    </DialogContentText>
                    {assignLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={30} sx={{ color: '#6C63FF' }} />
                        </Box>
                    ) : (
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {subAdmins.length > 0 ? subAdmins.map((admin) => (
                                <ListItem key={admin.subAdminId} dense sx={{ borderBottom: '1px solid #2E3247' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedSubAdmins.includes(admin.subAdminId)}
                                                onChange={() => handleSubAdminToggle(admin.subAdminId)}
                                                sx={{ '&.Mui-checked': { color: '#6C63FF' } }}
                                            />
                                        }
                                        label={<ListItemText primary={admin.name} secondary={admin.email} />}
                                        sx={{ width: '100%', m: 0 }}
                                    />
                                </ListItem>
                            )) : (
                                <ListItem><ListItemText primary="No sub-admins found" /></ListItem>
                            )}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSkipAssign} color="inherit">Skip</Button>
                    <Button onClick={handleAssignSubmit} variant="contained" disabled={selectedSubAdmins.length === 0}>
                        Assign ({selectedSubAdmins.length})
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
